'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Copy, RefreshCw, Check, FileText, Plus, Trash2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomCsvGeneratorContent } from '../../data/random-csv-generator-content';
import Link from 'next/link';

type CsvValue = string | number | boolean;

interface CsvField {
    key: string;
    type: 'string' | 'number' | 'boolean';
    min?: number;
    max?: number;
}

const DEFAULT_FIELDS: CsvField[] = [
    { key: 'id', type: 'number' },
    { key: 'name', type: 'string' },
    { key: 'email', type: 'string' },
    { key: 'active', type: 'boolean' },
    { key: 'score', type: 'number' },
];

const STRING_PREFIXES = [
    'user', 'item', 'product', 'order', 'customer', 'address', 'city', 'country',
    'description', 'title', 'name', 'first_name', 'last_name', 'email', 'phone'
];

const STRING_SUFFIXES = [
    'name', 'title', 'description', 'address', 'city', 'country', 'email', 'phone',
    'id', 'code', 'number', 'value', 'status', 'type'
];

const DOMAINS = ['example.com', 'test.com', 'demo.com', 'domain.com', 'sample.org'];

function generateRandomString(prefix = '') {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = prefix;

    if (!prefix) {
        const randomPrefix = STRING_PREFIXES[Math.floor(Math.random() * STRING_PREFIXES.length)];
        result = randomPrefix + '_';
    }

    const length = 3 + Math.floor(Math.random() * 5);
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    if (Math.random() > 0.5) {
        result += '_' + STRING_SUFFIXES[Math.floor(Math.random() * STRING_SUFFIXES.length)];
    }

    return result;
}

function generateRandomEmail() {
    const username = generateRandomString('user');
    const domain = DOMAINS[Math.floor(Math.random() * DOMAINS.length)];
    return `${username}@${domain}`;
}

function generateRandomValue(field: CsvField): CsvValue {
    switch (field.type) {
        case 'string':
            if (field.key.toLowerCase().includes('email')) {
                return generateRandomEmail();
            }
            return generateRandomString(field.key);
        case 'number':
            const min = field.min || 0;
            const max = field.max || 1000;
            return Math.floor(min + Math.random() * (max - min + 1));
        case 'boolean':
            return Math.random() > 0.5;
        default:
            return '';
    }
}

function generateCsv(fields: CsvField[], rows: number): string {
    // Generate header row
    const headers = fields.map(field => {
        const escaped = field.key.replace(/"/g, '""');
        return escaped.includes(',') || escaped.includes('"')
            ? `"${escaped}"`
            : escaped;
    });

    // Generate data rows
    const dataRows = [];
    for (let i = 0; i < rows; i++) {
        const row = fields.map(field => {
            const value = generateRandomValue(field);
            const strValue = String(value);
            const escaped = strValue.replace(/"/g, '""');
            return escaped.includes(',') || escaped.includes('"')
                ? `"${escaped}"`
                : strValue;
        });
        dataRows.push(row.join(','));
    }

    return [headers.join(','), ...dataRows].join('\n');
}

function CsvFieldEditor({
    field,
    onChange,
    onDelete
}: {
    field: CsvField;
    onChange: (field: CsvField) => void;
    onDelete: () => void;
}) {
    return (
        <div className="flex items-center gap-2 mb-2">
            <div className="grid gap-1 flex-1">
                <div className="flex gap-2">
                    <Input
                        placeholder="Field name"
                        value={field.key}
                        onChange={(e) => onChange({ ...field, key: e.target.value })}
                        className="flex-1"
                    />
                    <select
                        value={field.type}
                        onChange={(e) => onChange({ ...field, type: e.target.value as 'string' | 'number' | 'boolean' })}
                        className="w-32 px-3 py-2 border rounded-md"
                    >
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="boolean">Boolean</option>
                    </select>
                    {field.type === 'number' && (
                        <div className="flex gap-2">
                            <Input
                                type="number"
                                placeholder="Min"
                                value={field.min ?? ''}
                                onChange={(e) => onChange({
                                    ...field,
                                    min: e.target.value ? Number(e.target.value) : undefined
                                })}
                                className="w-20"
                            />
                            <Input
                                type="number"
                                placeholder="Max"
                                value={field.max ?? ''}
                                onChange={(e) => onChange({
                                    ...field,
                                    max: e.target.value ? Number(e.target.value) : undefined
                                })}
                                className="w-20"
                            />
                        </div>
                    )}
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={onDelete}
                        className="text-destructive hover:text-destructive"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default function RandomCsvGenerator() {
    const [fields, setFields] = useState<CsvField[]>(DEFAULT_FIELDS);
    const [rows, setRows] = useState<number>(10);
    const [csvData, setCsvData] = useState<string>('');
    const [copied, setCopied] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const { toast } = useToast();

    // Use refs to track current values
    const fieldsRef = useRef(fields);
    const rowsRef = useRef(rows);

    // Update refs when state changes
    useEffect(() => {
        fieldsRef.current = fields;
    }, [fields]);

    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    const generateCsvData = useCallback(() => {
        setIsGenerating(true);
        try {
            const result = generateCsv(fieldsRef.current, rowsRef.current);
            setCsvData(result);
        } catch (error) {
            console.error('Error generating CSV:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate CSV data. Please check your field configurations.',
                variant: 'destructive',
            });
        } finally {
            setIsGenerating(false);
        }
    }, [toast]);

    // Initial data generation
    useEffect(() => {
        generateCsvData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update CSV when fields or rows change
    const isMounted = useRef(false);
    useEffect(() => {
        if (isMounted.current) {
            generateCsvData();
        } else {
            isMounted.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields, rows]);

    const handleAddField = () => {
        setFields([...fields, { key: `field_${fields.length + 1}`, type: 'string' }]);
    };

    const handleFieldChange = (index: number, updatedField: CsvField) => {
        const newFields = [...fields];
        newFields[index] = updatedField;
        setFields(newFields);
    };

    const handleDeleteField = (index: number) => {
        if (fields.length > 1) {
            const newFields = [...fields];
            newFields.splice(index, 1);
            setFields(newFields);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(csvData);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast({
            title: 'Copied!',
            description: 'CSV data has been copied to clipboard.',
        });
    };

    const handleDownload = () => {
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'random_data.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast({
            title: 'Download started',
            description: 'Your CSV file is being downloaded.',
        });
    };

    return (
        <div className="container mx-auto py-8">
            <div className="grid gap-6">
                <ToolPageIntro title="Random CSV Generator">
                Generate random CSV data with our free online tool. Create test datasets, sample spreadsheets, and mock data for development and testing.
            </ToolPageIntro>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>CSV Configuration</CardTitle>
                            <CardDescription>
                                Define the structure of your CSV data
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="rows">Number of Rows</Label>
                                    <Input
                                        id="rows"
                                        type="number"
                                        min="1"
                                        max="10000"
                                        value={rows}
                                        onChange={(e) => setRows(Math.min(10000, Math.max(1, parseInt(e.target.value) || 1)))}
                                        className="mt-1"
                                    />
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <Label>Fields</Label>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleAddField}
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add Field
                                        </Button>
                                    </div>
                                    <div className="space-y-2">
                                        {fields.map((field, index) => (
                                            <CsvFieldEditor
                                                key={index}
                                                field={field}
                                                onChange={(updatedField) => handleFieldChange(index, updatedField)}
                                                onDelete={() => handleDeleteField(index)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                        <CardFooter className="flex justify-between">
                            <Button
                                variant="outline"
                                onClick={() => setFields(DEFAULT_FIELDS)}
                            >
                                Reset to Default
                            </Button>
                            <Button
                                onClick={generateCsvData}
                                disabled={isGenerating}
                            >
                                {isGenerating ? (
                                    <>
                                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Regenerate
                                    </>
                                )}
                            </Button>
                        </CardFooter>
                    </Card>

                    <Card className="h-full flex flex-col">
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <div>
                                    <CardTitle>CSV Output</CardTitle>
                                    <CardDescription>
                                        {rows} row{rows !== 1 ? 's' : ''}, {fields.length} column{fields.length !== 1 ? 's' : ''}
                                    </CardDescription>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!csvData}
                                    >
                                        {copied ? (
                                            <Check className="h-4 w-4 mr-1 text-green-500" />
                                        ) : (
                                            <Copy className="h-4 w-4 mr-1" />
                                        )}
                                        {copied ? 'Copied!' : 'Copy'}
                                    </Button>
                                    <Button
                                        variant="default"
                                        size="sm"
                                        onClick={handleDownload}
                                        disabled={!csvData}
                                    >
                                        <FileText className="h-4 w-4 mr-1" />
                                        Download CSV
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="flex-1 overflow-hidden">
                            <div className="relative h-full">
                                <Textarea
                                    value={csvData}
                                    readOnly
                                    className="h-full min-h-[300px] font-mono text-sm"
                                    placeholder="Your CSV data will appear here..."
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomCsvGeneratorContent.title}
                description={randomCsvGeneratorContent.description}
                content={randomCsvGeneratorContent.content}
                faqs={randomCsvGeneratorContent.faqs}
                relatedTools={randomCsvGeneratorContent.relatedTools}
            />

            <Toaster />
        </div>
    );
}